import { getSubscriptionRecord } from "./subscriptionUtils";

const MEDIA_DEFAULTS = {
  maxProductImages: 10,
  maxProductVideos: 5,
  maxProductCatalogs: 5,
  maxProductImageSizeMB: 5,
  maxProductVideoSizeMB: 50,
  maxProductCatalogSizeMB: 20,
  maxApplicationImages: 10,
  maxApprovedApplicationImages: 10,
  maxCaseStudyAttachments: 10,
};

const toMetric = (raw) => {
  const used = Number(raw?.used) || 0;
  const limit = Number(raw?.limit) || 0;
  const remaining =
    raw?.remaining != null && raw.remaining !== ""
      ? Math.max(0, Number(raw.remaining) || 0)
      : Math.max(limit - used, 0);

  return {
    used,
    limit,
    remaining,
    display: raw?.display || `${used}/${limit}`,
    atLimit: limit <= 0 || used >= limit,
  };
};

const ACTIVE_STATUSES = new Set(["active", "trial"]);

const isActiveSubscription = (subscription) => {
  if (!subscription) return false;
  if (subscription.isActive === true) return true;
  if (subscription.isActive === false) return false;

  const status = String(subscription.status || "").toLowerCase();
  if (!ACTIVE_STATUSES.has(status)) return false;

  const validUntil = subscription.endDate || subscription.trialEndDate;
  if (validUntil) {
    const end = new Date(validUntil);
    if (!Number.isNaN(end.getTime()) && end <= new Date()) return false;
  }

  return Boolean(subscription.plan || subscription.planName || subscription.subscriptionId);
};

export const getPlanCreationLimits = (response, options = {}) => {
  const subscription = getSubscriptionRecord(response);
  const isReady = options.isReady !== false;
  const hasPlan = isReady && isActiveSubscription(subscription);

  const users = toMetric(subscription?.users);
  const products = toMetric(subscription?.products);
  const caseStudies = toMetric(subscription?.caseStudies);
  const applicationImages = toMetric(
    subscription?.applicationImages?.approved || subscription?.applicationImages,
  );

  const canCreateResource = (metric, backendFlag) => {
    if (!isReady || !hasPlan || metric.atLimit) return false;
    if (typeof backendFlag === "boolean") return backendFlag;
    return true;
  };

  return {
    hasPlan,
    isReady,
    subscription,
    users: {
      ...users,
      canCreate: canCreateResource(users, subscription?.canAddUsers),
    },
    products: {
      ...products,
      canCreate: canCreateResource(products, subscription?.canAddProducts),
    },
    caseStudies: {
      ...caseStudies,
      canCreate: canCreateResource(caseStudies, subscription?.canAddCaseStudies),
    },
    applicationImages: {
      ...applicationImages,
      canApprove: canCreateResource(
        applicationImages,
        subscription?.canApproveApplicationImages,
      ),
    },
  };
};

export const getPlanLimitMessage = (resource, limits) => {
  if (!limits?.isReady) return "";
  if (!limits.hasPlan) {
    if (resource === "applicationImages") {
      return "You can view all application images. An active subscription is required to approve them.";
    }
    return "An active subscription is required to add this. Ask your super admin to assign a plan.";
  }

  const metric = limits[resource];
  if (!metric) return "Plan limit reached. Upgrade your plan to add more.";
  if (metric.atLimit) {
    if (resource === "applicationImages") {
      return `You can still view pending, approved, and rejected images. Approval limit reached (${metric.display}). Upgrade your plan to approve more.`;
    }
    return `Plan limit reached (${metric.display}). Upgrade your plan to add more.`;
  }
  if (resource === "applicationImages") {
    return `Viewing is unlimited. ${metric.display} approved on your current plan.`;
  }
  return `${metric.display} used on your current plan`;
};

const toCap = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

export const getPlanMediaLimits = (response, options = {}) => {
  const creation = getPlanCreationLimits(response, options);
  const raw =
    creation.subscription?.limits ||
    creation.subscription?.plan?.limits ||
    {};

  const resolve = (key) => {
    if (!creation.isReady) return Number.POSITIVE_INFINITY;
    if (!creation.hasPlan) return 0;
    return toCap(raw[key], MEDIA_DEFAULTS[key]);
  };

  return {
    ...creation,
    maxProductImages: resolve("maxProductImages"),
    maxProductVideos: resolve("maxProductVideos"),
    maxProductCatalogs: resolve("maxProductCatalogs"),
    maxProductImageSizeMB: resolve("maxProductImageSizeMB"),
    maxProductVideoSizeMB: resolve("maxProductVideoSizeMB"),
    maxProductCatalogSizeMB: resolve("maxProductCatalogSizeMB"),
    maxApplicationImages: resolve("maxApplicationImages"),
    maxApprovedApplicationImages: resolve("maxApprovedApplicationImages"),
    maxCaseStudyAttachments: resolve("maxCaseStudyAttachments"),
  };
};

export const formatPlanCap = (used, max, unit = "") => {
  if (!Number.isFinite(max)) return `${used}${unit}`;
  return `${used} / ${max}${unit}`;
};

export const filterFilesForPlanLimit = ({
  files = [],
  currentCount = 0,
  maxCount,
  maxSizeMB,
  itemLabel = "files",
}) => {
  const incoming = Array.from(files || []);
  const accepted = [];
  let error = "";
  let count = currentCount;

  for (const file of incoming) {
    if (Number.isFinite(maxCount) && count >= maxCount) {
      error = `Your plan allows ${maxCount} ${itemLabel}. Remove some to add more.`;
      break;
    }
    if (
      Number.isFinite(maxSizeMB) &&
      maxSizeMB > 0 &&
      file?.size != null &&
      file.size > maxSizeMB * 1024 * 1024
    ) {
      error = `${file.name || "File"} is too large. Max size is ${maxSizeMB}MB.`;
      continue;
    }
    accepted.push(file);
    count += 1;
  }

  return { accepted, error };
};
